import React, { useState, useEffect, useRef, useCallback } from "react";
// 把列表放进去触底加载
// Loading list while scrolling to bottom
interface ScrollContainerProps {
  onLoadMore: () => Promise<void>;
  hasMore: boolean;
  renderContent: React.ReactNode;
  renderNoMore?: React.ReactNode;
  loadingText?: string;
  containerHeight?: string;
  containerStyle?: React.CSSProperties;
}

const ScrollContainer = ({
  onLoadMore,
  hasMore,
  renderContent,
  renderNoMore = <div></div>,
  loadingText = "loading...",
  containerHeight = "100%",
  containerStyle,
}: ScrollContainerProps) => {
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const bottomSentinelRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) {
      return;
    }
    setIsLoadingMore(true);
    try {
      await onLoadMore();
    } catch (error) {
      console.error("error:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, onLoadMore]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          handleLoadMore();
        }
      },
      {
        root: containerRef.current,
        rootMargin: "0px",
        threshold: 0.1,
      }
    );

    if (bottomSentinelRef.current) {
      observer.observe(bottomSentinelRef.current);
    }

    return () => {
      if (bottomSentinelRef.current) {
        observer.unobserve(bottomSentinelRef.current);
      }
      observer.disconnect();
    };
  }, [handleLoadMore]);

  return (
    <div
      ref={containerRef}
      style={{
        height: containerHeight,
        overflowY: "auto",
        paddingBottom: "8px",
        ...containerStyle,
      }}
      className="infinite-scroll-container"
    >
      {renderContent}
      {isLoadingMore && hasMore && (
        <div style={{ textAlign: "center", padding: "20px", color: "#666" }}>{loadingText}</div>
      )}
      {!hasMore && renderNoMore}
      <div
        ref={bottomSentinelRef}
        className="infinite-scroll-bottom-sentinel"
      />
    </div>
  );
};

export default ScrollContainer;
